import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AnalyticsBloc } from '@kanalytics';
import { EnvironmentVariablesService } from '@kenv';
import { AuthenticationBloc } from '@kp/auth/authentication.bloc';
import { GroupValidationBloc } from '@kp/auth/group-validation.bloc';
import { OnboardingBloc } from '@kp/onboarding/onboarding.bloc';
import { AuthDataService, BrowserStorage, RouterHelperService } from '@kservice';
import { Product, UserType } from '@ktypes/enums';
import { OnboardingData } from '@ktypes/models';

@Injectable({
  providedIn: 'root',
})
export class NotAuthenticatedGuard {
  constructor(
    private _analyticsBloc: AnalyticsBloc,
    private authBloc: AuthenticationBloc,
    private authDataService: AuthDataService,
    private browserStorage: BrowserStorage,
    private environmentVariablesService: EnvironmentVariablesService,
    private onboardingBloc: OnboardingBloc,
    private groupValidationBloc: GroupValidationBloc,
    public router: Router,
    private routerHelper: RouterHelperService
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const onboardingData: OnboardingData = this.browserStorage.getObject('onboarding');
    const authData = this.authDataService.loadToken();
    const groupCode = route?.queryParams?.groupCode || route?.params?.groupCode;
    const { dataToken, source } = route?.queryParams || route?.params?.groupCode || {};

    // if there is a source or dataToken query param, navigation will be handled with other guards, exit
    if (source || dataToken) {
      return false;
    }

    // if group code and no onboarding data (or Resourceful), validate group code and go to landing
    if (
      groupCode &&
      (!onboardingData ||
        onboardingData.groupCode !== groupCode ||
        this.environmentVariablesService.product === Product.resourceful)
    ) {
      if (authData?.user?.type !== UserType.user) {
        this.groupValidationBloc.startValidatingGroupCode(groupCode);
      }
      void this.router.navigate(['/welcome'], { replaceUrl: true });
      return false;
    } else if (
      groupCode &&
      onboardingData &&
      onboardingData.groupCode === groupCode &&
      this.environmentVariablesService.product === Product.purposeful
    ) {
      // if group code and onboarding data, restore onboarding position
      this.onboardingBloc.restoreSavedOnboardingValues(onboardingData);
      void this.router.navigate([`/${onboardingData.latestPage}`], { replaceUrl: true });
      return false;
    } else if (authData?.token && authData?.user?.type === UserType.user) {
      this._analyticsBloc.userReadyForAnalytics();
      const isRouteValid = this.routerHelper.isRouteValid(
        route.url
          ?.filter((url) => url.path !== 'initial')
          .map((url) => url.path)
          .join('/')
      );
      return this.router.parseUrl(!isRouteValid ? '/error' : '');
    }

    // TODO: ensure this check tries to refresh the authData if expired
    // TODO: maintain path for proper redirect after login?
    // otherwise, just allow the non-authenticated user to continue on
    if (
      !authData ||
      (!authData.token && (!authData.user || !(authData.user.type === UserType.user))) ||
      this.authBloc.isTokenExpiredLocally()
    ) {
      return true;
    }
    // fallback, just resend to root path and following routing rules from there
    return this.router.parseUrl('');
  }
}
